// Profile migrations — v2 (Acceptance Pass).
//
// One-shot, silent on app boot. Idempotent (re-running is a no-op).
//
// What it does:
//   For every existing Well Profile that still carries currentMd/currentTvd
//   (the dynamic depth fields that have since moved to OperationalState),
//   AND for which no OpState has been created yet, materialize a new
//   OperationalState named "Migrated · {wellLabel}" and pull the dynamic
//   fields into it. The dynamic fields stay on the Well row so the
//   composer's backward-compat fallback can keep using them if no OpState
//   is picked.

import { listProfiles, createProfile } from "./storage";
import { emptyOpStateProfile } from "./schemas";
import { scoped } from "@/lib/accountScope";

const FLAG_KEY = "rsc.profiles.migration.v2";
const LOG_KEY  = "rsc.profiles.migration.v2.log";

export function runV2MigrationOnce() {
  try {
    if (scoped.getItem(FLAG_KEY) === "complete") return { ran: false };
    const wells   = listProfiles("well");
    const opStates = listProfiles("opstate");
    const log = [];
    for (const w of wells) {
      const wp = w.payload || {};
      const hasDynamic = wp.currentMd || wp.currentTvd || wp.holeSize;
      if (!hasDynamic) continue;
      // Skip if there's already an OpState linked to this well's label.
      const alreadyMigrated = opStates.some(
        (o) => (o.payload?.label || "").startsWith(`Migrated · ${wp.label}`)
      );
      if (alreadyMigrated) continue;
      const draft = {
        ...emptyOpStateProfile(),
        label:                `Migrated · ${wp.label || "(unnamed well)"}`,
        units:                wp.units || "IMP",
        currentMd:            wp.currentMd || "",
        currentTvd:           wp.currentTvd || "",
        holeSize:             wp.holeSize || "",
        capturedAt:           new Date().toISOString(),
        notes:                "Auto-migrated from Well Profile on " + new Date().toLocaleDateString(),
      };
      // We also pull mud density from any Mud profile with the same units —
      // best-effort, not strictly required for backward-compat.
      const muds = listProfiles("mud");
      const probe = muds.find((m) => (m.payload?.units || "IMP") === draft.units);
      if (probe?.payload?.density) draft.currentMudDensity = probe.payload.density;
      try {
        const created = createProfile("opstate", draft);
        log.push({ wellId: w.id, wellLabel: wp.label, opStateId: created.id });
      } catch (e) {
        log.push({ wellId: w.id, wellLabel: wp.label, error: e.message });
      }
    }
    scoped.setItem(FLAG_KEY, "complete");
    scoped.setItem(LOG_KEY, JSON.stringify({
      ranAt: new Date().toISOString(),
      created: log.length,
      detail: log,
    }));
    return { ran: true, created: log.length, detail: log };
  } catch (e) {
    return { ran: false, error: e.message };
  }
}
